/**
 * G10. Эмиграция: «Граница». При решении `move.abroad` — три вопроса
 * офицера (цель поездки, средства, возвращение), на каждый три ответа в
 * перемешанном порядке; «верный» подсвечивается (`hint`): про цель и
 * возвращение — при знаниях ≥ BORDER_HINT_KNOWLEDGE, про средства — при
 * деньгах ≥ ВВП на душу (цена переезда). При спонсоре (`flags.sponsor`)
 * вопрос о средствах снимается. Исход — поправка к шансу эмиграции из
 * решения: все ответы верные +10 %, все неверные −10 %, иначе 0
 * (`flags.abroadChanceDelta`, складывается с «чемоданом»).
 *
 * Ответы — «ехать»; `input.stay` — остаться. «Пропустить» — автовыбор
 * решения без поправки.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import { FLAG_ABROAD_CHANCE_DELTA, FLAG_SPONSOR, type Life, type MinigameOutcome, type Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { gdpPpp } from '../casino/common';
import { optionAvailable, pendingOf, shuffle } from '../skill/common';
import { nothing, numberFlag } from './common';

export const BORDER_QUESTIONS = ['purpose', 'means', 'return'] as const;
export type BorderQuestionId = (typeof BORDER_QUESTIONS)[number];
export const BORDER_ANSWERS = 3;
/** Индекс верного ответа в банке (до перемешивания). */
export const BORDER_CORRECT_INDEX = 0;
export const BORDER_DELTA = 0.1;
export const BORDER_HINT_KNOWLEDGE = 50;
/** Деньги в долях ВВП на душу, с которых подсвечивается ответ о средствах. */
export const BORDER_HINT_MONEY_GDP = 1;
/** Вопрос, который снимает спонсор. */
export const BORDER_SPONSOR_SKIPS: BorderQuestionId = 'means';

export interface BorderQuestion {
  id: BorderQuestionId;
  key: string;
  /** Ключи ответов в показанном порядке. */
  answers: string[];
  correct: number;
  hint: number | null;
}

export interface BorderState {
  questions: BorderQuestion[];
  delta: number;
  timeoutOption: string;
}

export type BorderInput = { picks: number[]; stay?: boolean };

const BORDER_KINDS = ['move.abroad'] as const;

export function borderQuestionKey(id: string): string {
  return `minigame.border.${id}`;
}

export function borderAnswerKey(id: string, i: number): string {
  return `minigame.border.${id}.${i}`;
}

/** Поправка по числу неверных ответов из `total`: 0 → +delta, все → −delta, иначе 0. */
export function borderDelta(wrong: number, total: number, delta: number = BORDER_DELTA): number {
  if (total <= 0) return 0;
  if (wrong === 0) return delta;
  return wrong >= total ? -delta : 0;
}

export const border: MinigameDef<BorderInput, BorderState> = {
  id: 'border',
  section: 'G',
  triggers: [{ kind: 'decision', decision: 'move.abroad' }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return pendingOf(life, BORDER_KINDS) !== null;
  },
  setup(life: Life, ds: Dataset, rng: Rng): BorderState {
    const sponsor = life.flags[FLAG_SPONSOR] === true;
    const knows = life.stats.knowledge >= BORDER_HINT_KNOWLEDGE;
    const funded = Math.max(0, life.money) >= BORDER_HINT_MONEY_GDP * gdpPpp(ds, life);
    const questions: BorderQuestion[] = [];
    for (const id of BORDER_QUESTIONS) {
      if (sponsor && id === BORDER_SPONSOR_SKIPS) continue;
      const order = shuffle([0, 1, 2], rng);
      const correct = order.indexOf(BORDER_CORRECT_INDEX);
      const hinted = id === 'means' ? funded : knows;
      questions.push({ id, key: borderQuestionKey(id), answers: order.map((i) => borderAnswerKey(id, i)), correct, hint: hinted ? correct : null });
    }
    return { questions, delta: BORDER_DELTA, timeoutOption: pendingOf(life, BORDER_KINDS)?.timeoutOption ?? 'stay' };
  },
  play(state: BorderState, input: BorderInput, life: Life): MinigameOutcome {
    const decision = pendingOf(life, BORDER_KINDS);
    const canGo = decision ? optionAvailable(decision, 'go') : true;
    if (input.stay === true || !canGo) return { ...nothing(), decisionOption: 'stay' };
    const picks = Array.isArray(input.picks) ? input.picks : [];
    const wrong = state.questions.filter((q, i) => picks[i] !== q.correct).length;
    const delta = borderDelta(wrong, state.questions.length, state.delta);
    const outcome: MinigameOutcome = {
      ...nothing(),
      decisionOption: 'go',
      logKey: delta > 0 ? 'minigame.border.calm' : delta < 0 ? 'minigame.border.tense' : 'minigame.border.ok',
    };
    if (delta !== 0) outcome.flags = { [FLAG_ABROAD_CHANCE_DELTA]: numberFlag(life, FLAG_ABROAD_CHANCE_DELTA) + delta };
    return outcome;
  },
  expected(state: BorderState): MinigameOutcome {
    return { ...nothing(), decisionOption: state.timeoutOption };
  },
};
